import { DEFAULT_LEVEL_NAMES, type Rubric, type RubricLevel } from './rubric';

/**
 * The rubrics a teacher can start from. Pure.
 *
 * The rubrics follow the criteria schools already use: the Smarter Balanced
 * argumentative, explanatory and narrative structure, the 6 Traits, and the usual AP-style
 * rhetorical-analysis criteria. Every level description here is written for this tool, as a
 * situation Jev can recognize rather than a degree; none is copied from a published rubric.
 */

export interface LibraryRubric {
  id: string;
  /** One line for the picker: what kind of writing it is for. */
  note: string;
  rubric: Rubric;
}

/** Four level texts, low to high, under the default level names. */
const levels = (...texts: [string, string, string, string]): RubricLevel[] =>
  texts.map((text, i) => ({ name: DEFAULT_LEVEL_NAMES[i]!, text }));

const ARGUMENTATIVE: Rubric = {
  version: 1,
  title: 'Argumentative Writing',
  assignment: 'Write an argument on a question of justice or public life. State a clear position, support it with reasons and evidence, answer the strongest objection, and write for a reader who does not already agree.',
  passLevel: 2,
  items: [
    {
      id: 'claim',
      name: 'Claim and Focus',
      description: 'The writing takes a clear position on the question and keeps every part of the piece working toward it.',
      levels: levels(
        'No position can be found, or the writing drifts between topics without arguing for anything.',
        'A position appears, but it is vague, stated only at the end, or abandoned for long stretches.',
        'A clear position is stated early and most of the writing supports it.',
        'A precise, arguable position is stated early and every paragraph advances it; the writer makes the stakes plain.',
      ),
    },
    {
      id: 'evidence',
      name: 'Evidence and Reasoning',
      description: 'Reasons, facts, examples and appeals support the claim, and the writer explains how each one proves it.',
      levels: levels(
        'The writing repeats the opinion or offers feelings in place of reasons; no evidence is given.',
        'One or two general reasons are given, with little evidence and no explanation of why the evidence matters.',
        'Several relevant reasons are supported with specific evidence, and the writer explains how most of it supports the claim.',
        'Specific, well-chosen evidence supports every reason, and the writer reasons from it — drawing out what it proves and why it should move the reader.',
      ),
    },
    {
      id: 'counterclaim',
      name: 'Counterclaim',
      description: 'The writing acknowledges an opposing view and answers it.',
      levels: levels(
        'No opposing view is mentioned.',
        'An opposing view is mentioned in passing but not answered, or it is dismissed without a reason.',
        'A reasonable opposing view is stated fairly and answered with a reason.',
        'The strongest opposing view is stated in its best form and answered so that the answer itself strengthens the claim.',
      ),
    },
    {
      id: 'organization',
      name: 'Organization',
      description: 'The order of ideas, paragraphs and transitions makes the argument easy to follow and builds toward a conclusion.',
      levels: levels(
        'Ideas appear in no discernible order; there are no paragraphs or transitions.',
        'Some grouping of ideas, but the order is loose and the reader has to supply connections.',
        'Paragraphs each develop one reason, transitions link them, and the ending follows from the argument.',
        'The structure itself persuades: each part sets up the next, and the conclusion lands the argument rather than restating it.',
      ),
    },
    {
      id: 'conventions',
      name: 'Language and Conventions',
      description: 'Word choice, tone, sentence variety and correct grammar and punctuation suit an argument for a serious reader.',
      levels: levels(
        'Frequent errors in grammar, spelling or punctuation make the writing hard to read.',
        'The writing is readable, but errors are common and the tone is casual or repetitive.',
        'A formal, consistent tone, varied sentences and few errors, none of which interfere with meaning.',
        'Precise word choice and controlled, varied sentences — including deliberate rhetorical devices — with essentially no errors.',
      ),
    },
  ],
};

const EXPLANATORY: Rubric = {
  version: 1,
  title: 'Explanatory Writing',
  assignment: 'Explain a process, a phenomenon or an idea to a reader who knows little about it. Introduce the topic, develop it with facts, definitions and examples, and leave the reader understanding why it works the way it does.',
  passLevel: 2,
  items: [
    {
      id: 'focus',
      name: 'Focus and Controlling Idea',
      description: 'The writing introduces a topic and a controlling idea about it, and stays on that idea.',
      levels: levels(
        'No topic is introduced, or the writing wanders across unrelated subjects.',
        'A topic is named, but there is no controlling idea; the writing lists facts about it.',
        'The topic and a controlling idea are clear early, and the writing stays on them.',
        'The writing opens with a question or idea that makes the reader want the explanation, and every section answers it.',
      ),
    },
    {
      id: 'development',
      name: 'Development',
      description: 'Facts, definitions, concrete details, examples and comparisons develop the topic.',
      levels: levels(
        'Few or no facts; statements are general or incorrect.',
        'Some facts are given, but they are thin, unexplained or not connected to the controlling idea.',
        'Relevant facts, definitions and examples develop the topic, and the writer explains how they connect.',
        'Well-chosen facts and examples, including comparisons to familiar things, build the reader’s understanding step by step.',
      ),
    },
    {
      id: 'organization',
      name: 'Organization',
      description: 'Ideas are grouped and sequenced so each part prepares the next, with transitions that show how ideas relate.',
      levels: levels(
        'Ideas appear in no discernible order.',
        'Some grouping, but the sequence is confusing or the reader must supply connections.',
        'A logical sequence with paragraphs and transitions; the ending follows from the explanation.',
        'The sequence mirrors how understanding is built: each step depends on the last, and the ending shows what the whole explanation adds up to.',
      ),
    },
    {
      id: 'language',
      name: 'Precise Language',
      description: 'The writing uses precise, subject-specific vocabulary and defines terms the reader may not know.',
      levels: levels(
        'Vague or incorrect words; terms are misused.',
        'Mostly general language; technical terms appear without definition.',
        'Precise vocabulary suited to the subject, with key terms defined.',
        'Exact vocabulary introduced at the moment it is needed and defined in plain words, so the reader learns the terms along with the ideas.',
      ),
    },
    {
      id: 'conventions',
      name: 'Conventions',
      description: 'Grammar, spelling, punctuation and sentence structure are correct.',
      levels: levels(
        'Frequent errors make the writing hard to read.',
        'Errors are common but the meaning is mostly clear.',
        'Few errors, none of which interfere with meaning.',
        'Essentially no errors, with varied sentence structures used well.',
      ),
    },
  ],
};

const NARRATIVE: Rubric = {
  version: 1,
  title: 'Narrative Writing',
  assignment: 'Write a narrative about an experience that changed how you understood something. Establish the situation and narrator, tell the events in a clear sequence, use description and dialogue, and reflect on what the experience meant.',
  passLevel: 2,
  items: [
    {
      id: 'situation',
      name: 'Situation and Point of View',
      description: 'The narrative establishes a setting, a narrator and a situation the reader can enter.',
      levels: levels(
        'No setting or situation is established; the reader cannot tell who, where or when.',
        'A setting or narrator is named but not developed.',
        'The setting, narrator and situation are clear from the opening.',
        'The opening places the reader inside the situation and establishes a distinct narrating voice.',
      ),
    },
    {
      id: 'sequence',
      name: 'Sequence and Pacing',
      description: 'Events unfold in a clear sequence, with time spent where it matters.',
      levels: levels(
        'Events are confused or out of order.',
        'Events are in order but listed at one speed; important moments are rushed.',
        'A clear sequence with transitions that signal shifts in time, and more time on the key moments.',
        'The pacing is controlled: the writer slows down for the moments that matter and moves briskly through the rest.',
      ),
    },
    {
      id: 'techniques',
      name: 'Narrative Techniques',
      description: 'Dialogue, description and the narrator’s thoughts develop the experience and the people in it.',
      levels: levels(
        'The writing summarizes; there is no dialogue or description.',
        'Some description or dialogue, but the writing mostly tells what happened.',
        'Dialogue, description and the narrator’s thoughts show the experience and the people in it.',
        'Techniques are chosen with purpose: a remembered line of speech or a single concrete detail carries a whole relationship or idea.',
      ),
    },
    {
      id: 'language',
      name: 'Precise and Sensory Language',
      description: 'Precise words and sensory details convey the experience.',
      levels: levels(
        'Vague, general words; no sensory detail.',
        'Some specific words, but description is generic.',
        'Precise words and sensory details make the events vivid.',
        'Exact, memorable language — including figurative language — makes the reader feel the experience.',
      ),
    },
    {
      id: 'reflection',
      name: 'Reflection and Conclusion',
      description: 'The ending reflects on what the experience meant and follows from the events.',
      levels: levels(
        'The narrative stops without an ending.',
        'The ending states a lesson that does not follow from the events.',
        'The ending reflects on the experience and follows from what happened.',
        'The reflection reaches beyond the event to a larger understanding the narrative has earned.',
      ),
    },
  ],
};

const SIX_TRAITS: Rubric = {
  version: 1,
  title: '6 Traits of Writing',
  assignment: 'Write a personal essay about an ordinary object, place or moment and what it reveals. Any form is welcome.',
  passLevel: 2,
  items: [
    {
      id: 'ideas',
      name: 'Ideas',
      description: 'The writing has a clear main idea, developed with details that matter.',
      levels: levels(
        'No main idea can be found; details are random.',
        'A main idea is present but general, and details are predictable.',
        'A clear main idea supported by relevant, specific details.',
        'A fresh, focused idea developed with details that surprise the reader and reveal something new.',
      ),
    },
    {
      id: 'organization',
      name: 'Organization',
      description: 'The structure, from the opening to the ending, shows the main idea.',
      levels: levels(
        'No clear beginning, middle or end.',
        'A recognizable structure, but the order is formulaic or the transitions are weak.',
        'An inviting opening, a logical order and a satisfying ending.',
        'The structure itself carries meaning: the ending recasts the opening.',
      ),
    },
    {
      id: 'voice',
      name: 'Voice',
      description: 'A person is behind the words; the writing has personality and suits its audience.',
      levels: levels(
        'Flat, mechanical writing with no sense of a writer.',
        'Occasional moments of personality in otherwise sincere but generic writing.',
        'A consistent, engaged voice that fits the purpose.',
        'An unmistakable voice — humor, conviction or wonder — that makes the reader want to keep reading.',
      ),
    },
    {
      id: 'word-choice',
      name: 'Word Choice',
      description: 'Words are specific, accurate and chosen to create a picture or make a point.',
      levels: levels(
        'Vague or misused words.',
        'Correct but ordinary words; little precision.',
        'Specific, accurate words, with strong verbs and nouns.',
        'Striking, exact language and fresh figures of speech, with nothing overdone.',
      ),
    },
    {
      id: 'fluency',
      name: 'Sentence Fluency',
      description: 'Sentences vary in length and structure and read smoothly aloud.',
      levels: levels(
        'Choppy, run-on or incomplete sentences make the writing hard to read aloud.',
        'Sentences are correct but similar in length and pattern.',
        'Varied sentence lengths and beginnings with a natural rhythm.',
        'Rhythm is used for effect: short sentences land points, long ones build momentum.',
      ),
    },
    {
      id: 'conventions',
      name: 'Conventions',
      description: 'Spelling, punctuation, capitalization and grammar are correct.',
      levels: levels(
        'Frequent errors get in the way of reading.',
        'Noticeable errors, but the meaning is clear.',
        'Few errors, none of which interfere with meaning.',
        'Conventions are controlled and used for effect, with essentially no errors.',
      ),
    },
  ],
};

const RHETORICAL_ANALYSIS: Rubric = {
  version: 1,
  title: 'Rhetorical Analysis',
  passLevel: 2,
  items: [
    {
      id: 'thesis',
      name: 'Thesis',
      description: 'The response states a defensible thesis about how the writer’s choices serve their purpose.',
      levels: levels(
        'No thesis, or the thesis only restates the prompt or summarizes the text.',
        'A thesis names devices or praises the text but does not say how the choices serve a purpose.',
        'A defensible thesis connects the writer’s choices to their purpose.',
        'A precise thesis that explains how the choices work together to achieve the purpose for this audience.',
      ),
    },
    {
      id: 'analysis',
      name: 'Analysis of Choices',
      description: 'The response explains how specific choices — structure, diction, repetition, imagery, appeals — create meaning and effect.',
      levels: levels(
        'Summarizes what the text says rather than how it says it.',
        'Identifies devices but explains their effect vaguely or not at all.',
        'Explains how several specific choices affect the audience and serve the purpose.',
        'Explains how choices build on each other across the whole text, including how its structure moves the audience.',
      ),
    },
    {
      id: 'evidence',
      name: 'Textual Evidence',
      description: 'The response quotes or closely paraphrases the text to support each claim.',
      levels: levels(
        'No reference to the text, or references that misquote it.',
        'Some quotation, but the quotations are dropped in without explanation.',
        'Accurate, relevant quotations support each claim and are explained.',
        'Short, precisely chosen quotations are woven into the writer’s own sentences, and each one is analyzed.',
      ),
    },
    {
      id: 'context',
      name: 'Context and Audience',
      description: 'The response accounts for the occasion, the audience and the purpose, and gets the facts of the situation right.',
      levels: levels(
        'Misstates the occasion, the speaker or the purpose.',
        'Mentions the occasion or audience but does not connect them to the choices.',
        'Correctly describes the occasion and audience and uses them to explain the choices.',
        'Shows how the writer anticipates the audience’s situation and turns the occasion to a larger purpose.',
      ),
    },
    {
      id: 'organization',
      name: 'Organization and Style',
      description: 'The response is organized around its claims, written clearly and correctly.',
      levels: levels(
        'Disorganized or hard to follow; frequent errors.',
        'Follows the order of the text rather than the order of its own argument; some errors.',
        'Organized by claims, with clear transitions and few errors.',
        'A controlled, fluent structure in which each paragraph advances the thesis.',
      ),
    },
  ],
};

export const RUBRIC_LIBRARY: readonly LibraryRubric[] = [
  { id: 'argumentative', note: 'Takes and defends a position.', rubric: ARGUMENTATIVE },
  { id: 'explanatory', note: 'Explains a topic to a reader new to it.', rubric: EXPLANATORY },
  { id: 'narrative', note: 'Tells and reflects on an experience.', rubric: NARRATIVE },
  { id: 'six-traits', note: 'An all-purpose rubric for any kind of writing.', rubric: SIX_TRAITS },
  { id: 'rhetorical-analysis', note: 'Analyzes how a speech or essay works.', rubric: RHETORICAL_ANALYSIS },
];

/** What a first visit, or a stored rubric that cannot be read, starts with. */
export const DEFAULT_RUBRIC: Rubric = ARGUMENTATIVE;

export type LibraryRubricId = string;

export const libraryRubric = (id: LibraryRubricId): Rubric | undefined =>
  RUBRIC_LIBRARY.find((entry) => entry.id === id)?.rubric;

/** The library id of a rubric as shipped, whatever its assignment says; undefined once edited. */
export const libraryIdOf = (rubric: Rubric): LibraryRubricId | undefined =>
  RUBRIC_LIBRARY.find((entry) => JSON.stringify({ ...entry.rubric, assignment: rubric.assignment }) === JSON.stringify(rubric))?.id;

export const isLibraryRubric = (rubric: Rubric): boolean => libraryIdOf(rubric) !== undefined;
